import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import SharedFooter from "@/components/SharedFooter";
import SharedHeader from "@/components/SharedHeader";
import { 
  Users, 
  GraduationCap, 
  School, 
  BookOpen, 
  ChevronRight,
  ArrowLeft,
  Star,
  Zap,
  Sparkles,
  Trophy,
  MessageCircle,
  Check
} from "lucide-react";

// Import extracted auth components
import { StudentLoginForm, ParentLoginForm, TeacherLoginForm, SchoolLoginForm } from "../components/auth";

// Floating animated elements for the hero side
function FloatingElement({ children, className, delay = 0 }) {
  return (
    <div 
      className={`absolute animate-float ${className}`}
      style={{ 
        animationDelay: `${delay}s`,
        animationDuration: '6s'
      }}
    >
      {children}
    </div>
  );
}

// Compact role button for the right side
function RoleButton({ role, title, subtitle, icon: Icon, iconBg, onClick, isExternal }) {
  return (
    <button
      onClick={onClick}
      className={`w-full group flex items-center gap-4 p-4 rounded-xl border-2 border-transparent bg-white hover:border-emerald-200 hover:bg-emerald-50/50 transition-all duration-300 shadow-sm hover:shadow-md text-left ${
        isExternal ? 'border-dashed border-slate-200' : ''
      }`}
      data-testid={`role-btn-${role}`}
    >
      <div className={`w-12 h-12 rounded-xl ${iconBg} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-slate-800">{title}</span>
          {isExternal && (
            <span className="text-[10px] bg-teal-100 text-teal-700 px-1.5 py-0.5 rounded-full font-medium">B2B</span>
          )}
        </div>
        <p className="text-sm text-slate-500 truncate">{subtitle}</p>
      </div>
      <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-emerald-500 group-hover:translate-x-1 transition-all" />
    </button>
  );
}

export default function UniversalLogin() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedRole, setSelectedRole] = useState(null);
  const [triggerRegistration, setTriggerRegistration] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [redirectTo, setRedirectTo] = useState(null);
  
  // Check URL params for registration mode, plan selection, and redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const isRegister = params.get('register') === 'true';
    const redirectParam = params.get('redirect');
    
    // Handle registration mode - always go to parent registration
    if (isRegister) {
      setSelectedRole('parent');
      setTriggerRegistration(true);
    }
    
    // Handle plan selection (for EduWise subscriptions)
    const planParam = params.get('plan');
    if (planParam && ['starter', 'plus', 'pro'].includes(planParam)) {
      setSelectedPlan(planParam);
    }
    
    // Handle redirect parameter (e.g., homework)
    // Only auto-select student if NOT in registration mode
    if (redirectParam) {
      setRedirectTo(redirectParam);
      if (redirectParam === 'homework' && !isRegister) {
        setSelectedRole('student');
      }
    }
  }, []);

  const handleBack = () => {
    setSelectedRole(null);
    setTriggerRegistration(false);
    window.history.replaceState({}, '', '/login');
  };

  const roles = [
    {
      role: "student",
      title: "Student",
      subtitle: "Access learning dashboard",
      icon: GraduationCap,
      iconBg: "bg-gradient-to-br from-emerald-500 to-teal-600"
    },
    {
      role: "parent",
      title: "Parent",
      subtitle: "Monitor child's progress",
      icon: Users,
      iconBg: "bg-gradient-to-br from-blue-500 to-indigo-600"
    }
  ];

  const schoolRoles = [
    {
      role: "teacher",
      title: "Teacher",
      subtitle: "View class performance",
      icon: BookOpen,
      iconBg: "bg-gradient-to-br from-violet-500 to-purple-600"
    }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      {/* Unified Header - same as web pages */}
      <SharedHeader showNav={true} />

      {/* Main content area - two columns */}
      <div className="flex-1 flex flex-col lg:flex-row">
        {/* Left Side - Hero Section */}
        <div className="hidden lg:flex lg:w-1/2 xl:w-[55%] bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-600 relative overflow-hidden">
        {/* Animated background patterns */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-teal-300 rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>
        </div>
        
        {/* Grid pattern overlay */}
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}></div>

        {/* Floating elements - positioned in safe areas */}
        <FloatingElement className="top-[8%] right-[8%]" delay={0}>
          <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
            <Star className="w-7 h-7 text-yellow-300" />
          </div>
        </FloatingElement>
        <FloatingElement className="top-[45%] right-[5%]" delay={1.5}>
          <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
            <Zap className="w-6 h-6 text-amber-300" />
          </div>
        </FloatingElement>
        <FloatingElement className="bottom-[25%] right-[12%]" delay={3}>
          <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
            <Trophy className="w-5 h-5 text-orange-300" />
          </div>
        </FloatingElement>
        <FloatingElement className="bottom-[8%] right-[5%]" delay={2}>
          <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
            <MessageCircle className="w-6 h-6 text-cyan-300" />
          </div>
        </FloatingElement>

        {/* Main content - centered vertically */}
        <div className="relative z-10 flex flex-col justify-center h-full w-full px-12 xl:px-16 py-8">
          <div className="max-w-xl">
            <h1 className="text-4xl xl:text-5xl font-bold text-white leading-tight mb-4">
              Learn Smart.<br />
              <span className="text-emerald-200">Learn Easy.</span>
            </h1>
            
            <p className="text-lg text-emerald-100/90 max-w-md mb-8 leading-relaxed">
              AI-powered personalized learning for students in grades 5-8. 
              Practice anytime via WhatsApp or our web portal.
            </p>

            {/* USP Points */}
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-emerald-400/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check className="w-4 h-4 text-emerald-200" />
                </div>
                <p className="text-emerald-100">
                  <span className="font-semibold text-white">Adaptive Learning</span> — Questions adjust to your child's pace and skill level
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-emerald-400/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check className="w-4 h-4 text-emerald-200" />
                </div>
                <p className="text-emerald-100">
                  <span className="font-semibold text-white">WhatsApp Integration</span> — Learn on the go, no app download needed
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-emerald-400/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check className="w-4 h-4 text-emerald-200" />
                </div>
                <p className="text-emerald-100">
                  <span className="font-semibold text-white">Parent Dashboard</span> — Track progress and get detailed insights
                </p>
              </div>
            </div>

            {/* Why Eduzy link - centered */}
            <div className="mt-10 flex justify-center">
              <Link to="/why-eduzy" data-testid="why-eduzy-hero-link">
                <Button className="bg-white hover:bg-emerald-50 text-emerald-700 font-bold px-8 py-4 text-lg shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 rounded-xl border-2 border-emerald-200">
                  <Sparkles className="h-6 w-6 mr-3 text-emerald-500" />
                  Discover Why Eduzy
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Login Options */}
      <div className="flex-1 flex flex-col bg-slate-50">
        {/* Main content area - centered to align with left side */}
        <main className="flex-1 flex items-center justify-center px-6 py-6">
          <div className="w-full max-w-md">
            {!selectedRole ? (
              <>
                {/* Welcome text - Removed "Welcome Back", increased font size */}
                <div className="text-center mb-6">
                  <p className="text-xl font-medium text-slate-700">
                    Choose how you'd like to sign in
                  </p>
                </div>

                {/* Role buttons */}
                <div className="space-y-3 mb-4" data-testid="role-selection">
                  {roles.map((role) => (
                    <RoleButton
                      key={role.role}
                      {...role}
                      onClick={() => setSelectedRole(role.role)}
                    />
                  ))}
                </div>

                {/* Parent registration link */}
                <p className="text-center text-sm text-slate-500 mb-4">
                  New to Eduzy?{" "}
                  <button 
                    onClick={() => {
                      setSelectedRole('parent');
                      setTriggerRegistration(true);
                    }}
                    className="text-emerald-600 hover:text-emerald-700 font-medium"
                  >
                    Register as Parent
                  </button>
                </p>

                {/* Divider */}
                <div className="relative my-4">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-slate-200"></div>
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-slate-50 px-3 text-slate-400">For Schools</span>
                  </div>
                </div>

                {/* School section roles */}
                <div className="space-y-3">
                  {schoolRoles.map((role) => (
                    <RoleButton
                      key={role.role}
                      {...role}
                      onClick={() => setSelectedRole(role.role)}
                    />
                  ))}
                  
                  {/* School Portal button */}
                  <RoleButton
                    role="school"
                    title="School Portal"
                    subtitle="Admin & institutional access"
                    icon={School}
                    iconBg="bg-gradient-to-br from-slate-600 to-slate-800"
                    onClick={() => setSelectedRole("school")}
                    isExternal={true}
                  />
                </div>

                {/* School registration link */}
                <p className="text-center text-sm text-slate-500 mt-4">
                  Are you a school administrator?{" "}
                  <Link to="/school/register" className="text-teal-600 hover:text-teal-700 font-medium">
                    Register your school
                  </Link>
                </p>

                {/* Back to home link */}
                <div className="text-center mt-4 pt-3 border-t border-slate-100">
                  <Link 
                    to="/" 
                    className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-emerald-600 transition-colors"
                    data-testid="back-to-home"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Back to Home
                  </Link>
                </div>
              </>
            ) : (
              /* Login Form */
              <div>
                {/* Back button */}
                <button
                  onClick={handleBack}
                  className="flex items-center gap-2 text-slate-500 hover:text-slate-700 mb-6 group"
                  data-testid="back-to-roles"
                >
                  <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                  <span className="text-sm">Back to options</span>
                </button>

                {/* Form card */}
                <Card className="border-0 shadow-xl bg-white">
                  <CardContent className="p-6 sm:p-8">
                    {selectedRole === "student" && <StudentLoginForm onBack={handleBack} redirectTo={redirectTo} />}
                    {selectedRole === "parent" && <ParentLoginForm onBack={handleBack} startInRegisterMode={triggerRegistration} selectedPlan={selectedPlan} />}
                    {selectedRole === "teacher" && <TeacherLoginForm onBack={handleBack} />}
                    {selectedRole === "school" && <SchoolLoginForm onBack={handleBack} />}
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        </main>
      </div>
      </div>

      {/* Footer - spans full width */}
      <SharedFooter />

      {/* CSS for floating animation */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          25% { transform: translateY(-10px) rotate(2deg); }
          50% { transform: translateY(-5px) rotate(-1deg); }
          75% { transform: translateY(-15px) rotate(1deg); }
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
